import ExpanderList, { ExpanderListItem } from '@rio-cloud/rio-uikit/ExpanderList';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import {
    compoundManagementSlice,
    getActiveCompoundManagementView,
    getSelectedCompoundVehicle,
} from '../../../reducers/compoundManagementSlice';
import { CompoundManagementView, IncomingVehicle } from '../../compoundManagement.types';
import { ArrivalDetailsHeader } from '../../shared/sidebar/ArrivalDetailsHeader';
import { CancelledArrivalDetailsHeader } from '../../shared/sidebar/CancelledArrivalDetailsHeader';
import { CompoundLocationAndDestinationDetails } from '../../shared/sidebar/CompoundLocationAndDestinationDetails';
import { CompoundVehicleVIN } from '../../shared/sidebar/CompoundVehicleVIN';
import { VehicleDetails } from '../../shared/sidebar/VehicleDetails';
import { ExpectedArrivalDetails, ExpectedArrivalDetailsProps } from './ExpectedArrivalDetails';
import { CustomsInformationExpander } from '../../../sharedComponents/sidebar/CustomsInformationExpander';

type IncomingVehiclesSidebarInternalProps = {
    selectedVehicle: IncomingVehicle | undefined;
    closeSidebar: () => void;
};

export const IncomingVehiclesSidebar: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const selectedVehicle = useAppSelector(getSelectedCompoundVehicle);
    const activeManagementView = useAppSelector(getActiveCompoundManagementView);

    const closeSidebar = () => {
        dispatch(compoundManagementSlice.actions.deselectCompoundVehicle());
    };

    if (activeManagementView === CompoundManagementView.INCOMING_VEHICLES) {
        return (
            <IncomingVehiclesSidebarInternal
                key={selectedVehicle?.id}
                selectedVehicle={selectedVehicle as IncomingVehicle | undefined}
                closeSidebar={closeSidebar}
            />
        );
    }
};

const IncomingVehiclesSidebarInternal: FunctionComponent<IncomingVehiclesSidebarInternalProps> = ({
    selectedVehicle,
    closeSidebar,
}) => {
    const sidebarProperties = {
        showHeaderBorder: true,
        resizable: true,
        position: Sidebar.RIGHT,
        minWidth: 400,
        maxWidth: 550,
        width: 550,
        fly: true,
    };

    if (selectedVehicle !== undefined) {
        const title = <FormattedMessage id="outboundOrderBook.compoundManagement.common.sidebar.header.titleVehicle" />;

        const footerButton = (
            <div className="display-flex gap-10 flex-row-reverse">
                <button className="btn btn-default" type="button" onClick={closeSidebar}>
                    <FormattedMessage id="outboundOrderBook.common.button.close" />
                </button>
            </div>
        );

        return (
            <Sidebar
                {...sidebarProperties}
                closed={false}
                onClose={closeSidebar}
                title={title}
                titleClassName="padding-left-10"
                footer={footerButton}
                footerClassName="margin-right-10"
            >
                <CompoundVehicleVIN vehicle={selectedVehicle.vehicle} />
                <CompoundLocationAndDestinationDetails
                    compoundLocation={selectedVehicle.compoundLocation}
                    vehicle={selectedVehicle.vehicle}
                />
                <ExpanderList
                    items={[
                        buildExpectedArrivalDetailsItem({
                            compoundVehicleId: selectedVehicle.id,
                            compoundLocation: selectedVehicle.compoundLocation,
                            arrival: selectedVehicle.arrival,
                        }),
                    ]}
                    className="margin-20"
                    unmountOnExit={false}
                />
                <CustomsInformationExpander customsInformation={selectedVehicle.vehicle.customsInformation} />
                <VehicleDetails vehicle={selectedVehicle.vehicle} isCancelled={false} />
            </Sidebar>
        );
    } else {
        return <Sidebar {...sidebarProperties} closed />;
    }
};

const buildExpectedArrivalDetailsItem = (props: ExpectedArrivalDetailsProps): ExpanderListItem => {
    const isCancelled = props.arrival.isCancelled;

    return {
        id: 'arrival',
        open: true,
        header: isCancelled ? <CancelledArrivalDetailsHeader /> : <ArrivalDetailsHeader inputRequired />,
        body: <ExpectedArrivalDetails {...props} />,
        bodyClassName: 'border-top-only border-width-1 border-style-solid border-color-light',
        className: isCancelled ? 'list-group-item-danger' : 'list-group-item-primary',
    };
};
